import {
  AfterViewInit,
  Component,
  HostBinding,
  Inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatStep, MatStepper } from '@angular/material/stepper';
import * as _ from 'lodash';

import {
  CustomHoleSchemaTypeEnum,
  CuttingCustomHoleSchema,
} from '../../models/cutting/cutting-custom-hole-schema.model';
import { CuttingCustomHole } from '../../models/cutting/cutting-custom-hole.model';
import { CuttingFastenersSet, PositioningTypeEnum } from '../../models/cutting/cutting-fasteners-set.model';
import { CuttingFixedFastenersSet } from '../../models/cutting/cutting-fixed-fasteners-set.model';
import { PlateTypeEnum } from '../../models/cutting/cutting-panel-outline.model';
import { Position } from '../../models/cutting/cutting-svg.model';
import { PanelBase } from '../../models/cutting/panel-base.model';
import { Countertop } from '../../models/cutting/public-api';
import { CuttingPanelListService } from '../../modules/cutting-panel-list/cutting-panel-list.service';
import { CountertopSchemaComponent } from '../countertop-schema/countertop-schema.component';
import { CustomHoleParansForm } from './base-point-select/fastener-base-point-select.component';
import { FastenersWizardDialogResult, FastenersWizardDialogResultFasteners } from './fasteners-wizard-dialog.types';

export interface CountertopFastenersWizardDialogData {
  countertop: Countertop;
  mainMaterialThickness: number;
  holeSchemas: CuttingCustomHoleSchema[];
  holeSetSchemas: CuttingFastenersSet[];
  availablePlate?: PlateTypeEnum;
  defaultMirrorAxis?: 'x' | 'y' | 'xy';
}

@Component({
  selector: 'csl-countertop-fasteners-wizard-dialog',
  templateUrl: './countertop-fasteners-wizard-dialog.component.html',
  styleUrls: ['./countertop-fasteners-wizard-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CountertopFastenersWizardDialogComponent implements AfterViewInit {
  newHoles: CuttingCustomHole[] = [];
  newFastenerSets: CuttingFixedFastenersSet[] = [];
  newHolesSide: Position | undefined;
  multiSide: Position[] | undefined;
  selectedIndex = 0;
  selectedFastener: CuttingCustomHoleSchema | CuttingFastenersSet;

  @ViewChild(MatStepper, { static: true }) stepper: MatStepper;
  @ViewChild('schemaListStep', { static: true }) schemaListStep: MatStep;
  @ViewChild('sideSelectStep', { static: true }) sideSelectStep: MatStep;
  @ViewChild('basePointSelectStep', { static: true }) basePointSelectStep: MatStep;
  @ViewChild('edgeSchema', { static: true }) edgeSchema: CountertopSchemaComponent;

  get defaultMirrorAxis(): 'x' | 'y' | 'xy' {
    return this.data.defaultMirrorAxis || 'y';
  }

  get fastenerTitleName(): string {
    if (!this.selectedFastener) {
      return '';
    }
    let res = this.selectedFastener.name;

    if (this.selectedFastener instanceof CuttingCustomHoleSchema) {
      res += `, ${(this.selectedFastener as CuttingCustomHoleSchema).diameter}mm`;
      if (this.selectedFastener.maxDepth === 0 && this.selectedFastener.minDepth === 0) {
        res += `, ${$localize`Сквозное`}`;
      }
    }
    return `(${res})`;
  }

  constructor(
    public dialogRef: MatDialogRef<CountertopFastenersWizardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CountertopFastenersWizardDialogData
  ) {}

  ngAfterViewInit(): void {
    this.edgeSchema.render(this.data.countertop);
  }

  isFastenersSet(item: CuttingCustomHoleSchema | CuttingFastenersSet): item is CuttingFastenersSet {
    return item instanceof CuttingFastenersSet;
  }

  onSideChanged(side: Position): void {
    this.newHolesSide = side;

    this.sideSelectStep.completed = true;

    this.stepper.next();
  }

  onBasePointChanged(response: CustomHoleParansForm): void {
    if (response) {
      this.newHoles = [];
      this.newFastenerSets = [];

      this.multiSide = response.coords.map(c => c.multiSide);

      if (!this.isFastenersSet(this.selectedFastener)) {
        response.coords.forEach(coord => {
          const newHole = new CuttingCustomHole();

          newHole.schemaId = this.selectedFastener.id;
          newHole.name = this.selectedFastener.name;
          newHole.diameter = (this.selectedFastener as CuttingCustomHoleSchema).diameter;
          newHole.basePoint = coord.point;
          newHole.x = coord.x;
          newHole.y = coord.y;
          newHole.z = coord.z;
          newHole.depth = response.depth;
          newHole.type = response.type;

          this.newHoles.push(newHole);
        });
      }

      if (this.isFastenersSet(this.selectedFastener)) {
        response.coords.forEach(coord => {
          const newFixedFastenersSet: CuttingFixedFastenersSet = _.cloneDeep(
            this.selectedFastener
          ) as CuttingFixedFastenersSet;
          Object.setPrototypeOf(newFixedFastenersSet, CuttingFixedFastenersSet.prototype);

          newFixedFastenersSet.basePoint = coord.point;
          newFixedFastenersSet.plate = response.type;
          newFixedFastenersSet.x = coord.x;
          newFixedFastenersSet.y = coord.y;

          this.newFastenerSets.push(newFixedFastenersSet);
        });
      }

      this.basePointSelectStep.completed = true;
    } else {
      this.basePointSelectStep.completed = false;
    }
  }

  onFastenerChanged(fastener: CuttingCustomHoleSchema | CuttingFastenersSet): void {
    this.selectedFastener = fastener;

    if (!fastener) {
      return;
    }

    const goToBasePointStep = (): void => {
      this.newHolesSide = undefined;
      this.sideSelectStep.optional = true;
      this.stepper.selected = this.basePointSelectStep;
    };

    if (fastener instanceof CuttingCustomHoleSchema) {
      this.schemaListStep.completed = true;

      if (this.selectedFastener.type === CustomHoleSchemaTypeEnum.Edge) {
        this.stepper.next();
      } else {
        goToBasePointStep();
      }
    }

    if (fastener instanceof CuttingFastenersSet) {
      this.schemaListStep.completed = true;

      if (this.selectedFastener.type !== PositioningTypeEnum.Free) {
        this.stepper.next();
      } else {
        goToBasePointStep();
      }
    }
  }

  saveFastener(): void {
    // TODO: Rewrite this component

    const fastenersLength = Math.max(this.newHoles.length, this.newFastenerSets.length);
    const sides: Position[] = this.multiSide || new Array(fastenersLength).fill(this.newHolesSide);

    const result: FastenersWizardDialogResult = {};

    if (!_.isEmpty(this.newHoles)) {
      result.holes = this.newHoles.reduce(
        (acc, v, i) => [...acc, { side: sides[i], item: v }],
        []
      ) as FastenersWizardDialogResultFasteners<CuttingCustomHole>[];
    }

    if (!_.isEmpty(this.newFastenerSets)) {
      result.sets = this.newFastenerSets.reduce(
        (acc, v, i) => [...acc, { side: sides[i], item: v }],
        []
      ) as FastenersWizardDialogResultFasteners<CuttingFixedFastenersSet>[];
    }

    this.dialogRef.close(result);
  }
}
